import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get } from 'lodash';

import Helmet from 'react-helmet';
import msg from 'msg';

import BasketControls from '../../containers/BasketControls';
import BopAgeRestriction from './BopAgeRestriction';
import BopFoodProfileBasicInfo from 'apps/products/containers/partials/BopFoodProfileBasicInfo';
import BopFavouritesButton from './BopFavouritesButton';
import BopShoppingListButton from './BopShoppingListButton';
import BoughtDerivatives from './BoughtDerivatives';
import Coupon from './Coupon';
import Derivatives from '../../containers/Derivatives';
import getAnalyticsData from '../../utils/getAnalyticsData';
import isInStock from '../../utils/isInStock';
import Metadata from './Metadata';
import OutOfStock from './OutOfStock';
import Price from './Price';
import ProductLabel from '../../models/ProductLabel';
import Regulars from '../../containers/Regulars';
import SmartPass from './SmartPass';

import { ITEM_UNAVAILABLE_ERROR } from './Derivatives';
import { Offer } from './Offer';
import PricePromise from 'apps/products/components/partials/PricePromise';

export default class BopBasicInfo extends Component {
  static propTypes = {
    addToFavourites: PropTypes.func.isRequired,
    analyticsContextData: PropTypes.object,
    childSku: PropTypes.string,
    hasAlternatives: PropTypes.bool,
    hasDerivatives: PropTypes.bool,
    openShoppingListsModal: PropTypes.func.isRequired,
    product: PropTypes.object,
    productDetails: PropTypes.object,
    productsBySku: PropTypes.object,
    pageMetaData: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
    }),
    removeFromFavourites: PropTypes.func.isRequired,
    router: PropTypes.object,
    showRegulars: PropTypes.bool,
    showZerosAfterPounds: PropTypes.bool,
    productWarnings: PropTypes.object,
    onSeeAlternativesClick: PropTypes.func.isRequired,
    isFlashSale: PropTypes.bool,
    usePredefinedLabelForEDLP: PropTypes.bool,
    moreExclusiveOffer: PropTypes.shape({
      moreOffersUrl: PropTypes.string,
      isMoreCardCustomer: PropTypes.bool,
    }),
  };

  isBasketControlsActive = () => {
    const { productDetails: {
      backOfPack: { derivatives } = {}, options,
    }, productWarnings = {}, childSku } = this.props;
    if (derivatives && (!options || derivatives.dimensions.length !== options.length)) {
      return false;
    }

    if (productWarnings[childSku] && productWarnings[childSku].includes(ITEM_UNAVAILABLE_ERROR)) {
      return false;
    }

    return true;
  };

  lastBoughtDate = (product) =>
    !!product.lastBoughtDate && product.lastBoughtDate
      .replace(/(\d{4})-(\d{1,2})-(\d{1,2})/, '$3/$2/$1')
      .replace('/20', '/');

  renderRegulars = (sku) => {
    const { isLoggedIn, showRegulars } = this.props;
    const shouldShowRegulars = isLoggedIn && showRegulars;

    return shouldShowRegulars && (
      <div className="bop-regulars">
        <Regulars sku={sku} />
      </div>
    );
  };

  renderProductPreferencesButtons = () => {
    const { isLoggedIn, addToFavourites, removeFromFavourites, router } = this.props;
    const { product, productDetails } = this.props;
    const isInFavourites = !!(product.labels && product.labels.includes('FAVOURITE'));
    const isAgeRestricted = productDetails.backOfPack && productDetails.backOfPack.salesRestrictionType === 'AGE';

    if (!isLoggedIn) {
      return isAgeRestricted ? (
        <section className="bop-section bop-favouritesButtonGroup">
          <div className="bop-favouritesButtonGroup__wrapper">
            <BopAgeRestriction />
          </div>
        </section>
      ) : null;
    }

    return (
      <section className="bop-section bop-favouritesButtonGroup">
        <div className="bop-favouritesButtonGroup__wrapper">
          {isAgeRestricted && <BopAgeRestriction />}
          <BopFavouritesButton
            addToFavourites={addToFavourites}
            inFavourites={isInFavourites}
            isLoggedIn={isLoggedIn}
            product={product}
            removeFromFavourites={removeFromFavourites}
            router={router}
            className="bop-favouritesButton"
          />
          <BopShoppingListButton
            isLoggedIn={isLoggedIn}
            onShoppingListsModalOpen={this.close}
            openShoppingListsModal={this.props.openShoppingListsModal}
            product={product}
            router={router}
            className="bop-shoppinglistButton"
          />
        </div>
      </section>);
  };

  renderBasketControls = (product, backOfPack) => {
    const { analyticsContextData, productDetails, hasDerivatives, childSku, isFlashSale } = this.props;
    const isBasketControlsActive = this.isBasketControlsActive();
    const { basketItems, router } = this.props;
    const derivativesCount = get(backOfPack, 'derivatives.derivativesTree', []).length;

    return (
      <ul className={classNames('bop-basketControls', { 'bop-basketControls--derivatives': hasDerivatives })}>
        {hasDerivatives && (
          <Derivatives
            sku={product.sku}
            childSku={childSku}
            {...backOfPack.derivatives}
          />
        )}
        <li className={classNames({ 'bop-basketControls__item--derivatives': hasDerivatives })}>
          <BasketControls
            analyticsContextData={analyticsContextData}
            sku={hasDerivatives && isBasketControlsActive ? childSku : product.sku}
            basketItems={basketItems}
            disabled={!isBasketControlsActive}
            product={product}
            router={router}
            productDetails={productDetails}
            hasDerivatives={hasDerivatives}
            hideControls={hasDerivatives && derivativesCount > 1}
            isFlashSale={isFlashSale}
          />
        </li>
      </ul>
    );
  };

  renderPromotion = (product, promotion) => {
    if (!promotion) {
      return false;
    }

    const { offer, labels = [] } = product;
    const { usePredefinedLabelForEDLP } = this.props;
    const isEdlp = labels.includes(ProductLabel.EDLP);
    const edlpMessage = !usePredefinedLabelForEDLP && offer
      ? offer.text
      : msg.fop.offer.edlp;
    const { description } = promotion;

    if (isEdlp) {
      return (
        <Offer text={edlpMessage} isClickable={false} edlpDescription={description} />
      );
    }

    return (
      <Offer {...promotion} isClickable />
    );
  }

  renderBasicInfoInStock = (product, backOfPack, moreExclusiveOffer) => {
    const { coupon, sku } = product;
    const { offersDetails: { promotion, smartPass } = {}, derivatives } = backOfPack;
    const { basketItems, router, productsBySku, hasDerivatives } = this.props;
    const isSmartPassEligibility = smartPass && smartPass.type === 'SMART_PASS_ELIGIBLE';
    const isCouponApplied = !!basketItems.find((el) => el.coupon && el.coupon.skuCouponWasAddedTo === product.sku);
    const { valueDelivered: isValueDeliveredProduct } = product;
    const isCouponFreeToUse = (couponId) =>
      !basketItems.find(item => item.coupon
        && couponId === item.coupon.id
        && item.sku !== product.sku);
    const lastBoughtDate = this.lastBoughtDate(product);
    const analyticsData = getAnalyticsData(product);

    return <>
      {this.renderBasketControls(product, backOfPack)}
      {lastBoughtDate &&
        <p className="gn-content__paragraph--small bop-latBoughtDate">Last bought: {lastBoughtDate}</p>
      }
      <div className="bop-offers">
        {this.renderPromotion(product, promotion)}
        {coupon && isCouponFreeToUse(coupon.id) && (
          <Coupon
            {...coupon}
            analyticsData={analyticsData}
            isCouponApplied={isCouponApplied}
            dispatch={this.props.dispatch}
            router={router}
            sku={product.sku}
          />)}
        {smartPass && !(isSmartPassEligibility && promotion) && (
          <SmartPass {...smartPass} moreExclusiveOffer={moreExclusiveOffer} />
        )}
      </div>
      {isValueDeliveredProduct && (
        <div
          className="opp-bop"
        >
          <PricePromise
            className="opp-bop"
            tooltipDefaultPositions={['below', 'center']}
            label={msg.product.oppLabel}
            showLabelIcon
          />
        </div>
      )}
      {hasDerivatives && (
        <BoughtDerivatives
          basketItems={basketItems}
          productsBySku={productsBySku}
          dispatch={this.props.dispatch}
          router={router}
          parentSku={sku}
          derivatives={derivatives}
        />
      )}
      {!hasDerivatives && this.renderRegulars(product.sku)}

      {!hasDerivatives && this.renderProductPreferencesButtons()}
    </>;
  };

  render = () => {
    const { product = this.props.product, backOfPack = {} } = this.props.productDetails;
    const { offersDetails: { smartPass } = {} } = backOfPack;
    const { deranged, gtin } = product;
    const isCustomerSubscribedToSmartPass = smartPass && smartPass.type === 'SMART_PASS';
    const { valueDelivered: isValueDeliveredProduct } = product;
    const { showZerosAfterPounds, pageMetaData, isFoodMaestroEnabled } = this.props;

    const moreExclusiveOffer = isCustomerSubscribedToSmartPass ? this.props.moreExclusiveOffer : {};

    return (
      <section className={classNames('bop-section', 'bop-basicInfo',
        { 'bop-outOfStock__section': !isInStock(product) })}
      >
        <Metadata
          backOfPack={backOfPack}
          pageMetaData={pageMetaData}
          product={product}
        />
        <div className="bop-price">
          <Price
            price={product.price}
            labels={product.labels}
            catchWeightMaxWeight={backOfPack.catchweightMaxWeight}
            catchWeightMinWeight={backOfPack.catchweightMinWeight}
            isCustomerSubscribedToSmartPass={isCustomerSubscribedToSmartPass}
            isValueDeliveredProduct={isValueDeliveredProduct}
            showZerosAfterPounds={showZerosAfterPounds}
          />
        </div>

        {isInStock(product) ?
          this.renderBasicInfoInStock(product, backOfPack, moreExclusiveOffer)
          :
          (<>
            <Helmet>
              <meta name="robots" content="noindex, follow" />
            </Helmet>
            <OutOfStock
              hasAlternatives={this.props.hasAlternatives}
              onSeeAlternativesClick={this.props.onSeeAlternativesClick}
              deranged={deranged}
            />
          </>
          )}
        {isFoodMaestroEnabled && <BopFoodProfileBasicInfo barcode={gtin} />}
      </section>
    );
  }
}
